import { apiClient } from '@/shared/lib/apiClient';
import { WEBSITE_ANALYTICS_TOP_PAGES_LIMIT } from '../constants';
import type {
  WebsiteAnalyticsConversionResponse,
  WebsiteAnalyticsDays,
  WebsiteAnalyticsDevicesResponse,
  WebsiteAnalyticsProductsResponse,
  WebsiteAnalyticsRecentEvent,
  WebsiteAnalyticsSourcesResponse,
  WebsiteAnalyticsSummary,
  WebsiteAnalyticsTopPagesResponse,
  WebsiteAnalyticsTrendsResponse,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

function daysParams(days: WebsiteAnalyticsDays): Record<string, string> {
  return { days: String(days) };
}

function productFilterParams(
  days: WebsiteAnalyticsDays,
  productSlug?: string,
): Record<string, string> {
  const params = daysParams(days);
  if (productSlug) {
    params.productSlug = productSlug;
  }
  return params;
}

function groupedParams(
  days: WebsiteAnalyticsDays,
  limit: number,
  productSlug?: string,
): Record<string, string> {
  return { ...productFilterParams(days, productSlug), limit: String(limit) };
}

export async function fetchWebsiteAnalyticsSummary(): Promise<WebsiteAnalyticsSummary> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsSummary>>(
    '/api/v1/website-analytics/summary',
  );
  return res.data;
}

export async function fetchWebsiteAnalyticsRecent(
  limit = 50,
): Promise<WebsiteAnalyticsRecentEvent[]> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsRecentEvent[]>>(
    '/api/v1/website-analytics/recent',
    { params: { limit: String(limit) } },
  );
  return res.data;
}

export async function fetchWebsiteAnalyticsProducts(
  days: WebsiteAnalyticsDays,
): Promise<WebsiteAnalyticsProductsResponse> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsProductsResponse>>(
    '/api/v1/website-analytics/products',
    { params: daysParams(days) },
  );
  return res.data;
}

export async function fetchWebsiteAnalyticsTrends(
  days: WebsiteAnalyticsDays,
  productSlug?: string,
): Promise<WebsiteAnalyticsTrendsResponse> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsTrendsResponse>>(
    '/api/v1/website-analytics/trends',
    { params: productFilterParams(days, productSlug) },
  );
  return res.data;
}

export async function fetchWebsiteAnalyticsTopPages(
  days: WebsiteAnalyticsDays,
  productSlug?: string,
  limit = WEBSITE_ANALYTICS_TOP_PAGES_LIMIT,
): Promise<WebsiteAnalyticsTopPagesResponse> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsTopPagesResponse>>(
    '/api/v1/website-analytics/top-pages',
    { params: groupedParams(days, limit, productSlug) },
  );
  return res.data;
}

export async function fetchWebsiteAnalyticsSources(
  days: WebsiteAnalyticsDays,
  productSlug?: string,
  limit = WEBSITE_ANALYTICS_TOP_PAGES_LIMIT,
): Promise<WebsiteAnalyticsSourcesResponse> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsSourcesResponse>>(
    '/api/v1/website-analytics/sources',
    { params: groupedParams(days, limit, productSlug) },
  );
  return res.data;
}

export async function fetchWebsiteAnalyticsDevices(
  days: WebsiteAnalyticsDays,
  productSlug?: string,
): Promise<WebsiteAnalyticsDevicesResponse> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsDevicesResponse>>(
    '/api/v1/website-analytics/devices',
    { params: productFilterParams(days, productSlug) },
  );
  return res.data;
}

export async function fetchWebsiteAnalyticsConversion(
  days: WebsiteAnalyticsDays,
  productSlug?: string,
): Promise<WebsiteAnalyticsConversionResponse> {
  const res = await apiClient.get<ApiResponse<WebsiteAnalyticsConversionResponse>>(
    '/api/v1/website-analytics/conversion',
    { params: productFilterParams(days, productSlug) },
  );
  return res.data;
}
